import { useEffect } from 'react';

import { useCrowdfundingProvider } from '#Context/index.js';
import { CampaignStoreItemTypes } from '#api/index.js';
import { EditPeerPageModalFlow } from '#components/organisms/modals/EditPeerPage/EditPeerPageModalFlow';
import { useUserAccessPermissions } from '@givebacks/api/core';
import {
  PeerTeamWith__Type,
  useShowCampaignPeer,
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import { EndedCampaignAlert } from 'core/Components';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { RichTextDisplay } from 'rich-text';
import {
  Box,
  ButtonMantine,
  Card,
  Container,
  DEFAULT_PEER_PROFILE_BANNER_IMAGE,
  Divider,
  FullWidthButtonGroup,
  Group,
  MobileBottomButton,
  Skeleton,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';

import { HeaderImage } from '../../../components';
import { PeerDonationCard } from '../../../components/Cards/PeerDonationCard';
import classes from '../../../components/CausePageLayout.module.css';
import { usePeerCreditModal } from '../../../components/Modals/usePeerCreditModal';
import { PeerRecentDonations } from '../RecentDonations/RecentDonations';
import { TeamHeaderActions } from './TeamHeaderActions';
import { TeamParticipants } from './TeamParticipants';

const TeamNotFound = () => {
  useEffect(() => {
    notifications.show({ variant: 'error', message: 'Team not found' });
  }, []);

  return <Navigate to="../participant-center" />;
};

const TeamPageActions = ({
  team,
  campaign,
}: {
  team: PeerTeamWith__Type;
  campaign: CampaignStoreItemTypes;
}) => {
  const navigate = useNavigate();
  const { handleSelectPeer } = usePeerCreditModal();

  const hasEditAccess = team?.can_write;

  return (
    <FullWidthButtonGroup wrap="wrap-reverse">
      <ButtonMantine variant="gradient" onClick={() => handleSelectPeer(team.uuid, 'teams')}>
        Donate
      </ButtonMantine>
      {hasEditAccess ? (
        <EditPeerPageModalFlow peer={team} campaign={campaign} />
      ) : campaign?.participant_options?.registration_open ? (
        <ButtonMantine onClick={() => navigate('register')}>
          {campaign?.participant_options.registration_call_to_action}
        </ButtonMantine>
      ) : null}
    </FullWidthButtonGroup>
  );
};

const TeamPage = () => {
  const { team_uuid } = useParams();
  const { causeId, campaignId } = useCrowdfundingProvider();

  const { data: campaign, isLoading: isCampaignLoading } = useShowConsumerCampaignById({
    causeId,
    campaignId,
  });

  const { data: settings } = useShowConsumerCampaignSettingsById({ campaignId, causeId });

  const { data: permissions } = useUserAccessPermissions({
    causeId,
    accessType: 'fundraiser',
  });

  const {
    data: team,
    isLoading: isTeamLoading,
    isError: isTeamError,
  } = useShowCampaignPeer({
    peerType: 'teams',
    peerId: team_uuid,
    causeId,
    campaignId,
    queryOptions: {
      options: {
        credits: true,
      },
    },
  });

  const isAuthenticatedTeam = team?.can_write;
  const hasFundraiserWriteAccess = permissions?.write;

  const hasEditAccess = isAuthenticatedTeam || hasFundraiserWriteAccess;

  if (!team || !campaign) return null;
  if (isTeamError) {
    return <TeamNotFound />;
  }

  return (
    <Container fluid className={classes.container}>
      <Stack className={classes.leftContent}>
        <HeaderImage
          isLoading={isCampaignLoading}
          src={team.image?.url ?? DEFAULT_PEER_PROFILE_BANNER_IMAGE}
          headerButtons={
            <TeamHeaderActions
              causeId={campaign.cause_id}
              campaignId={campaign.uuid}
              teamId={team.uuid}
              teamName={team.name}
              teamConvention={campaign?.team_options?.convention_singular}
              participantConvention={campaign?.participant_options?.convention_singular}
              hasEditAccess={hasEditAccess ?? false}
            />
          }
          backTo="../participant-center"
        />
        <Stack>
          <Skeleton visible={isTeamLoading}>
            <Title order={1}>{`${team.name} Fundraising Page`}</Title>
          </Skeleton>
          {team.description && (
            <Skeleton visible={isTeamLoading}>
              <Text type="body" style={{ whiteSpace: 'pre-wrap' }}>
                {team.description}
              </Text>
            </Skeleton>
          )}
        </Stack>
        {settings?.isCampaignReadOnly && <EndedCampaignAlert type="campaign" hiddenFrom="md" />}
        <PeerDonationCard type={team.__type} peer={team} campaign={campaign} />
        {(hasEditAccess || settings?.isParticipantsInCenterEnabled) && (
          <TeamParticipants teamName={team.name} />
        )}
        {campaign?.description && (
          <Skeleton visible={isCampaignLoading}>
            <Stack>
              <Title order={2}>About {campaign.name}</Title>
              <Text type="body" style={{ whiteSpace: 'pre-wrap' }}>
                <RichTextDisplay html={campaign?.description} />
              </Text>
            </Stack>
          </Skeleton>
        )}
        {(hasEditAccess || campaign?.show_recent_donations) && (
          <PeerRecentDonations
            uuid={team.uuid}
            causeId={campaign.cause_id}
            campaignId={campaign.uuid}
            peerType={'teams'}
          />
        )}
      </Stack>
      <Box className={classes.rightContent}>
        <Box className={classes.rightContentChild}>
          <Card p="xl" shadow="md">
            <Stack gap="lg">
              <Text c="gray" ta="center">
                Help {team.name} reach their goal!
              </Text>
              <Divider c="cobalt.0" />
              {settings?.isCampaignReadOnly ? (
                <EndedCampaignAlert type="campaign" />
              ) : (
                <Group gap="sm" grow>
                  <TeamPageActions team={team} campaign={campaign} />
                </Group>
              )}
            </Stack>
          </Card>
        </Box>
      </Box>
      {!settings?.isCampaignReadOnly && (
        <MobileBottomButton>
          <TeamPageActions team={team} campaign={campaign} />
        </MobileBottomButton>
      )}
    </Container>
  );
};

export { TeamPage };
